import React, { useEffect, useState } from 'react';
import { Typography, Table, Spin, message, Drawer, Button, Modal } from 'antd';
import { Link } from 'react-router-dom';
import {
  RightOutlined,
  PlusOutlined,
  ExclamationCircleFilled,
} from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import get from 'lodash/get';
import requireAuth from '../../../hocs/requireAuth';
import ContentPageHeader from '../../../components/contentPageHeader';
import { productMangement } from '../../../redux/actions/productAction';
import {
  generatePaginationObj,
  hasPermission,
} from '../../../utilities/helpers';
import {
  catalogueTableColumns,
  catalogueTableFiltersList,
} from '../../../constants/catalogue';
import { CREATE_PRODUCT } from '../../../constants/routes';
import {
  PRODUCT_DELETE_SUCCESS,
  PRODUCT_STATUS_UPDTAE_SUCCESS,
} from '../../../constants/messages';
import ProductDetails from '../../../components/product/productDetails';
import TableFilters from '../../../components/tableFilters';
import ShowPaginationLimits from '../../../components/showPaginationLimits';

const { Text } = Typography;
const { confirm } = Modal;

function CatalogueScreen() {
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const { permissions } = useSelector((state) => get(state, 'auth.role'));
  const selectedCompany = useSelector((state) =>
    get(state, 'auth.selectedCompany')
  );
  const role = useSelector((state) => get(state, 'auth.role'));

  const [viewProductDrawer, setViewProductDrawer] = useState({
    visible: false,
    data: null,
  });

  const toggleViewProductDrawer = (data = null) => {
    setViewProductDrawer({
      visible: !get(viewProductDrawer, 'visible'),
      data,
    });
  };

  const [tableData, setTableData] = useState({
    data: [],
    filters: null,
    sorters: null,
    pagination: {
      offset: 0,
      limit: 10,
      total: 0,
      current: 1,
    },
    filterChips: null,
  });
  const [categoryList, setCategoryList] = useState([]);
  const [tempFilter, setTempFilter] = useState({
    filterDropdown: {
      showMainCategory: false,
      showStatus: false,
      showProductType: false,
    },
    tempFilterChips: {
      category_id_1: undefined,
      is_active: undefined,
      is_sample: undefined,
    },
    filterValues: {
      dhandha_sku: undefined,
      name: undefined,
      category_id_1: undefined,
      is_active: undefined,
      is_sample: undefined,
    },
  });

  const { offset, limit } = generatePaginationObj(
    get(tableData, 'pagination.current'),
    get(tableData, 'pagination.limit')
  );

  const fetchMainCategoryList = () => {
    setLoading(true);
    dispatch(
      productMangement('list.product_categories', {
        company_id: get(selectedCompany, 'id'),
        filters: {
          type: 1,
        },
      })
    )
      .then((result) => {
        setLoading(false);
        setCategoryList(get(result, 'data'));
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const fetchProductCatalogue = (
    page = 1,
    filterObj = {},
    sorterObj = {},
    filterChipsObj = {},
    perPagelimit = get(tableData, 'pagination.limit')
  ) => {
    setLoading(true);
    const pageobj = generatePaginationObj(page, perPagelimit);
    dispatch(
      productMangement('list.products', {
        ...pageobj,
        company_id: get(selectedCompany, 'id'),
        ...sorterObj,
        filters: filterObj,
      })
    )
      .then((result) => {
        setTableData({
          data: get(result, 'data'),
          filters: filterObj,
          sorters: sorterObj,
          pagination: {
            ...pageobj,
            total: get(result, 'total'),
            current: page,
          },
          filterChips: filterChipsObj,
        });
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const onDeleteProduct = (id, companyId) => {
    setLoading(true);
    const payload = {
      company_id: companyId,
      id,
    };
    dispatch(productMangement('delete.product', payload))
      .then(() => {
        setLoading(false);
        message.success(PRODUCT_DELETE_SUCCESS);
        fetchProductCatalogue(get(tableData, 'pagination.current'));
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const onConfirmDelete = (id, companyId) => {
    confirm({
      title: 'Are you sure!',
      icon: <ExclamationCircleFilled />,
      content: 'Do you want to delete this Product?',
      onOk() {
        onDeleteProduct(id, companyId);
      },
      onCancel() {},
    });
  };

  const updateProductStatus = (id, isChecked, companyId) => {
    setLoading(true);
    const payload = {
      id,
      company_id: companyId,
      is_active: isChecked,
    };
    dispatch(productMangement('update.product', payload))
      .then(() => {
        setLoading(false);
        message.success(PRODUCT_STATUS_UPDTAE_SUCCESS);
        fetchProductCatalogue(get(tableData, 'pagination.current'));
      })
      .catch(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchProductCatalogue();
    fetchMainCategoryList();
  }, [selectedCompany]);

  return (
    <Spin spinning={loading}>
      <div>
        <div>
          <ContentPageHeader
            border
            title={[
              <Text className="content-page-header-heading">Product</Text>,
              <RightOutlined className="content-page-header-heading" />,
              <Text className="content-page-header-heading">Catalogue</Text>,
            ]}
            extra={[
              hasPermission(permissions, ['PERM_ADD_PRODUCT_CATALOG']) && (
                <Link to={CREATE_PRODUCT} key="1">
                  <Button
                    className="default-primary-btn"
                    type="primary"
                    icon={<PlusOutlined />}
                  >
                    Add New Product
                  </Button>
                </Link>
              ),
            ]}
          />
        </div>

        <div
          className="d-flex filter mt-20"
          style={{ alignItems: 'flex-start' }}
        >
          <TableFilters
            filtersList={catalogueTableFiltersList(categoryList)}
            tableData={tableData}
            fetchTableData={fetchProductCatalogue}
            tempFilter={tempFilter}
            setTempFilter={setTempFilter}
            roleCode={get(role, 'role_code')}
          />
        </div>

        <div
          className="d-flex filter"
          style={{ justifyContent: 'space-between' }}
        >
          <ShowPaginationLimits
            tableData={tableData}
            offset={offset}
            limit={limit}
            fetchTableData={fetchProductCatalogue}
            filterChips={get(tempFilter, 'tempFilterChips')}
          />
        </div>

        <div className="product-table-container">
          <Table
            size="small"
            rowKey="id"
            className="product-table"
            dataSource={get(tableData, 'data')}
            columns={catalogueTableColumns({
              onConfirmDelete,
              onViewDetails: toggleViewProductDrawer,
              updateProductStatus,
              hasEditPermission: hasPermission(permissions, [
                'PERM_UPDATE_PRODUCT_CATALOG',
              ]),
              hasDeletePermission: hasPermission(permissions, [
                'PERM_DELETE_PRODUCT_CATALOG',
              ]),
            })}
            onChange={(paginationObj, filterObj, sorterObj) => {
              fetchProductCatalogue(
                get(paginationObj, 'current'),
                get(tableData, 'filters'),
                get(sorterObj, 'order')
                  ? {
                      sortBy: get(sorterObj, 'field'),
                      sortDir:
                        get(sorterObj, 'order') === 'ascend' ? 'ASC' : 'DESC',
                    }
                  : {},
                get(tempFilter, 'tempFilterChips')
              );
            }}
            pagination={{
              showSizeChanger: false,
              position: ['bottomCenter'],
              hideOnSinglePage: true,
              pageSize: get(tableData, 'pagination.limit'),
              total: get(tableData, 'pagination.total'),
              current: get(tableData, 'pagination.current'),
            }}
          />
        </div>
      </div>
      <Drawer
        title="Product Overview"
        destroyOnClose
        placement="right"
        width="45%"
        onClose={toggleViewProductDrawer}
        open={get(viewProductDrawer, 'visible')}
      >
        <ProductDetails product={get(viewProductDrawer, 'data')} />
      </Drawer>
    </Spin>
  );
}

export default requireAuth(CatalogueScreen);
